import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '@/core/api'
import { logger } from '@/utils/logger'

export type Project = {
  id: string
  name: string
  description?: string
  view_preset?: string
  repositories?: unknown[]
  created_at?: string
  updated_at?: string
  [key: string]: unknown
}

export type SharedProject = Project & {
  role: string
  ownerName?: string
}

export const useProjectsStore = defineStore('projects', () => {
  const projects = ref<Project[]>([])
  const sharedProjects = ref<SharedProject[]>([])
  const currentProject = ref<Project | null>(null)
  const loading = ref(false)

  async function fetchProjects() {
    loading.value = true
    try {
      const result = await api.getProjects()
      // Handle both old (array) and new ({ ownProjects, sharedProjects }) response formats
      if (Array.isArray(result)) {
        projects.value = result as Project[]
        sharedProjects.value = []
      } else {
        const data = result as { ownProjects?: Project[]; sharedProjects?: SharedProject[] }
        projects.value = data.ownProjects ?? []
        sharedProjects.value = data.sharedProjects ?? []
      }
      return projects.value
    } catch (error) {
      logger.error('ProjectsStore', 'Failed to fetch projects:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function fetchProject(id: string) {
    loading.value = true
    try {
      const data = (await api.getProject(id)) as Project
      currentProject.value = data
      return data
    } catch (error) {
      logger.error('ProjectsStore', 'Failed to fetch project:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function createProject(data: Record<string, unknown>) {
    const project = (await api.createProject(data)) as Project
    projects.value.push(project)
    return project
  }

  async function updateProject(id: string, data: Record<string, unknown>) {
    const updated = (await api.updateProject(id, data)) as Project
    const idx = projects.value.findIndex(p => p.id === id)
    if (idx !== -1) projects.value[idx] = updated
    if (currentProject.value?.id === id) currentProject.value = updated
    return updated
  }

  async function deleteProject(id: string) {
    await api.deleteProject(id)
    projects.value = projects.value.filter(p => p.id !== id)
    if (currentProject.value?.id === id) currentProject.value = null
  }

  async function updateViewPreset(projectId: string, preset: string) {
    await api.updateProjectViewPreset(projectId, preset)
    if (currentProject.value?.id === projectId) {
      currentProject.value = { ...currentProject.value, view_preset: preset }
    }
  }

  function $reset() {
    projects.value = []
    sharedProjects.value = []
    currentProject.value = null
    loading.value = false
  }

  return {
    projects,
    sharedProjects,
    currentProject,
    loading,
    fetchProjects,
    fetchProject,
    createProject,
    updateProject,
    deleteProject,
    updateViewPreset,
    $reset,
  }
})
